// ===============================================================
// ProBank — Transaction PIN Pad
//
// One shared component used by every transaction page. Three modes:
//
//   enter  → user types their 4-digit PIN to authorize a payment
//   create → first-time setup (PIN + confirm)
//   reset  → forgot PIN (account password + new PIN + confirm)
//
// Handles server responses:
//   PIN_NOT_SET   → switches to "create" automatically
//   PIN_INCORRECT → shakes, shows attempts remaining
//   PIN_LOCKED    → shows lock message with countdown minutes
//
// Usage:
//   <PinPad
//     open={pinOpen}
//     brand={{ accent: "#0e9f6e", accentText: "#fff" }}
//     title="Authorize Transfer"
//     subtitle="₦25,000 to JOHN DOE"
//     onSuccess={(pin) => submitWithPin(pin)}
//     onClose={() => setPinOpen(false)}
//   />
// ===============================================================

import React, { useState, useEffect, useRef, useCallback } from "react";

const API_BASE = "https://alkebulanfkalertbackend.onrender.com/api";

export default function PinPad({
  open,
  onClose,
  onSuccess,
  title = "Enter Transaction PIN",
  subtitle = "",
  brand = {},
}) {
  const accent = brand.accent || "#0e9f6e";
  const accentText = brand.accentText || "#ffffff";

  const [mode, setMode] = useState("enter"); // enter | create | reset
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState(1); // create/reset: 1 = new pin, 2 = confirm
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);

  const resetState = useCallback(() => {
    setPin("");
    setConfirmPin("");
    setPassword("");
    setStep(1);
    setError("");
    setBusy(false);
    setMode("enter");
  }, []);

  // Check PIN status when opened — no PIN → straight to create mode
  useEffect(() => {
    if (!open) return;
    resetState();

    const checkStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}/pin/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && !data.hasPin) {
          setMode("create");
        }
      } catch {
        // If status check fails, stay in enter mode — the transfer
        // call will return PIN_NOT_SET and we handle it there too.
      }
    };
    checkStatus();
  }, [open, resetState]);

  // Auto-focus the hidden input
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open, mode, step]);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleDigit = (d) => {
    if (busy) return;
    setError("");
    const current = step === 2 ? confirmPin : pin;
    if (current.length >= 4) return;
    const next = current + d;
    if (step === 2) setConfirmPin(next);
    else setPin(next);
  };

  const handleDelete = () => {
    if (busy) return;
    setError("");
    if (step === 2) setConfirmPin((p) => p.slice(0, -1));
    else setPin((p) => p.slice(0, -1));
  };

  // Auto-submit when 4 digits are entered
  useEffect(() => {
    if (!open) return;

    if (mode === "enter" && pin.length === 4) {
      // Hand the PIN to the parent — the parent submits the transfer
      // and calls back with the server result via onSuccess's promise.
      handleEnterSubmit();
    } else if (mode === "create" || mode === "reset") {
      if (step === 1 && pin.length === 4) {
        setTimeout(() => setStep(2), 200);
      } else if (step === 2 && confirmPin.length === 4) {
        handleSetupSubmit();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, confirmPin]);

  const handleEnterSubmit = async () => {
    setBusy(true);
    try {
      // The parent's onSuccess performs the actual transfer with this
      // PIN and returns the fetch Response-like { ok, data }.
      const result = await onSuccess(pin);

      if (result?.ok) {
        onClose();
        resetState();
        return;
      }

      const code = result?.data?.code;
      const message = result?.data?.error || result?.data?.message || "Transaction failed.";

      if (code === "PIN_INCORRECT") {
        triggerShake();
        setPin("");
        setError(message);
      } else if (code === "PIN_LOCKED") {
        triggerShake();
        setPin("");
        setError(message);
      } else if (code === "PIN_NOT_SET") {
        setMode("create");
        setPin("");
        setStep(1);
        setError("");
      } else {
        // Other errors (balance, plan, etc.) — parent handles them;
        // just close and let the parent show its own blocker.
        onClose();
        resetState();
      }
    } catch {
      onClose();
      resetState();
    } finally {
      setBusy(false);
    }
  };

  const handleSetupSubmit = async () => {
    if (pin !== confirmPin) {
      triggerShake();
      setError("PINs do not match. Try again.");
      setConfirmPin("");
      return;
    }

    setBusy(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const isReset = mode === "reset";
      const endpoint = isReset ? "/pin/reset" : "/pin/set";
      const body = isReset
        ? { password, pin, confirmPin }
        : { pin, confirmPin };

      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        triggerShake();
        setError(data.message || "Could not save your PIN. Please try again.");
        setConfirmPin("");
        setStep(1);
        setPin("");
        setBusy(false);
        return;
      }

      // PIN saved — switch to enter mode so the user authorizes
      // the pending transaction with their brand-new PIN.
      setMode("enter");
      setPin("");
      setConfirmPin("");
      setStep(1);
      setError("");
      setBusy(false);
    } catch {
      setError("Network error. Please try again.");
      setBusy(false);
    }
  };

  if (!open) return null;

  const activePin = step === 2 ? confirmPin : pin;

  const headingText =
    mode === "create"
      ? step === 1
        ? "Create Transaction PIN"
        : "Confirm Your PIN"
      : mode === "reset"
      ? step === 1
        ? "Set a New PIN"
        : "Confirm New PIN"
      : title;

  const subText =
    mode === "create"
      ? "You'll use this 4-digit PIN to authorize every transaction."
      : mode === "reset"
      ? "Verified. Now choose a new 4-digit PIN."
      : subtitle;

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        zIndex: 200,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) { onClose(); resetState(); } }}
    >
      <div
        style={{
          width: "100%", maxWidth: 400, background: "#14171c",
          borderRadius: "20px 20px 0 0", padding: "20px 24px 36px",
          fontFamily: "'Inter', -apple-system, sans-serif",
          animation: "pinpad-up 0.25s ease-out",
        }}
      >
        <style>{`
          @keyframes pinpad-up { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
          @keyframes pinpad-shake { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-8px); } 40%,80% { transform: translateX(8px); } }
        `}</style>

        <div style={{ width: 40, height: 4, background: "rgba(255,255,255,0.15)", borderRadius: 4, margin: "0 auto 18px" }} />

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{
            width: 46, height: 46, margin: "0 auto 12px", borderRadius: "50%",
            background: `${accent}1a`, border: `1px solid ${accent}44`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill={accent} viewBox="0 0 24 24" style={{ width: 20, height: 20 }}>
              <path fillRule="evenodd" d="M12 1a4 4 0 014 4v3h1a3 3 0 013 3v9a3 3 0 01-3 3H7a3 3 0 01-3-3v-9a3 3 0 013-3h1V5a4 4 0 014-4zm0 2a2 2 0 00-2 2v3h4V5a2 2 0 00-2-2z" clipRule="evenodd" />
            </svg>
          </div>
          <div style={{ color: "#edeae3", fontWeight: 700, fontSize: 16 }}>{headingText}</div>
          {subText ? (
            <div style={{ color: "#8b8d93", fontSize: 12, marginTop: 4 }}>{subText}</div>
          ) : null}
        </div>

        {/* Reset mode — password step first */}
        {mode === "reset" && step === 1 && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, color: "#8b8d93", marginBottom: 6, fontWeight: 600 }}>
              ACCOUNT PASSWORD
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your login password"
              style={{
                width: "100%", padding: "13px 14px", borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.1)", background: "#0b0d10",
                color: "#edeae3", fontSize: 14, outline: "none", boxSizing: "border-box",
              }}
            />
            <div style={{ fontSize: 11, color: "#8b8d93", marginTop: 6 }}>
              Verify it's you before setting a new PIN.
            </div>
          </div>
        )}

        {/* PIN dots */}
        {!(mode === "reset" && step === 1 && !password) && (
          <div
            onClick={() => inputRef.current?.focus()}
            style={{
              display: "flex", justifyContent: "center", gap: 16, marginBottom: 18,
              animation: shake ? "pinpad-shake 0.4s" : "none",
              cursor: "text",
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  width: 16, height: 16, borderRadius: "50%",
                  background: activePin.length > i ? accent : "transparent",
                  border: `2px solid ${activePin.length > i ? accent : "rgba(255,255,255,0.25)"}`,
                  transition: "all 0.12s",
                }}
              />
            ))}
          </div>
        )}

        {/* Hidden input — triggers the phone's numeric keyboard */}
        <input
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          value={activePin}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
            setError("");
            if (step === 2) setConfirmPin(digits);
            else setPin(digits);
          }}
          style={{ position: "absolute", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
          aria-label="PIN input"
        />

        {error && (
          <div style={{ textAlign: "center", color: "#f87171", fontSize: 12, marginBottom: 14 }}>
            {error}
          </div>
        )}

        {/* Numeric keypad */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, maxWidth: 280, margin: "0 auto" }}>
          {["1","2","3","4","5","6","7","8","9","","0","⌫"].map((key, idx) => {
            if (key === "") return <div key={idx} />;
            const isDelete = key === "⌫";
            return (
              <button
                key={idx}
                onClick={() => (isDelete ? handleDelete() : handleDigit(key))}
                disabled={busy || (mode === "reset" && step === 1 && !password)}
                style={{
                  padding: "14px 0", borderRadius: 12, border: "none",
                  background: isDelete ? "transparent" : "rgba(255,255,255,0.06)",
                  color: "#edeae3", fontSize: isDelete ? 18 : 20, fontWeight: 600,
                  cursor: busy ? "wait" : "pointer",
                  opacity: busy || (mode === "reset" && step === 1 && !password) ? 0.4 : 1,
                }}
              >
                {key}
              </button>
            );
          })}
        </div>

        {/* Footer links */}
        <div style={{ textAlign: "center", marginTop: 18 }}>
          {mode === "enter" && (
            <button
              onClick={() => { setMode("reset"); resetState(); setMode("reset"); }}
              style={{ background: "none", border: "none", color: accent, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Forgot PIN?
            </button>
          )}
          {(mode === "create" || mode === "reset") && (
            <div style={{ fontSize: 11, color: "#8b8d93" }}>
              {mode === "create"
                ? "Keep it private — never share your PIN with anyone."
                : "After resetting, you'll authorize this transaction with your new PIN."}
            </div>
          )}
        </div>

        {/* Cancel */}
        <button
          onClick={() => { if (!busy) { onClose(); resetState(); } }}
          style={{
            width: "100%", marginTop: 16, padding: "12px", borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.1)", background: "none",
            color: "#8b8d93", fontWeight: 600, fontSize: 14, cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
